'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { getHiddenFromFiltersUnitAccess } from '@repo/api';
import { useStore } from '@/providers/StoreProvider';
import UnitPageLoading from '../loading';

/**
 * Entitlement gate for units flagged `isHiddenFromFilters`.
 *
 * The server can't make this call — the entitlement is per-renter and the
 * session lives client-side — so the page ships behind a loader and this
 * decides on mount: signed out goes home, signed in asks the listing service.
 *
 * Only mounted when the listing is actually flagged. An ordinary unit page
 * renders its children directly and never sees a loader.
 */

type Decision = 'pending' | 'allowed' | 'denied';

export function HiddenUnitGate({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const sessionHydrated = useStore(s => s.webAuth.sessionHydrated);
  const isLoggedIn = useStore(s => s.webAuth.isLoggedIn);
  const [decision, setDecision] = useState<Decision>('pending');

  useEffect(() => {
    /*
     * Decide NOTHING until the persisted store has rehydrated.
     *
     * `isLoggedIn` is itself a persisted key, so before rehydration it still
     * holds its initial `false` — reading it early reports a signed-in renter
     * as signed out and bounces them to the home page a tick before their
     * session lands. `sessionHydrated` is flipped by the store's
     * `onRehydrateStorage`, so it is the only safe trigger.
     *
     * Nothing flips `isLoggedIn` asynchronously after that point: the web app
     * has no `onAuthStateChanged` subscription, and the only other writer is
     * the cross-tab `storage` listener, which this effect re-runs for.
     */
    if (!sessionHydrated) return;

    if (!isLoggedIn) {
      setDecision('denied');
      return;
    }

    let cancelled = false;

    void (async () => {
      try {
        const { hasAccess } = await getHiddenFromFiltersUnitAccess();
        if (!cancelled) setDecision(hasAccess ? 'allowed' : 'denied');
      } catch {
        // Fail closed. An entitlement we could not confirm is not an
        // entitlement — a failed call must not open a hidden unit.
        if (!cancelled) setDecision('denied');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionHydrated, isLoggedIn]);

  // `replace`, not `push`: the gated URL should not sit in history for the
  // back button to land on again.
  useEffect(() => {
    if (decision === 'denied') router.replace('/');
  }, [decision, router]);

  // Loader covers both `pending` and `denied` — the latter is only ever a
  // frame or two before the redirect commits, and showing the unit in that
  // gap would defeat the gate.
  if (decision !== 'allowed') return <UnitPageLoading />;

  return <>{children}</>;
}
